import { useState } from 'react';
import type { PaginationConfig } from '@/components/common/PaginatedGentlyTable';

/**
 * Offset pagination state for `PaginatedGentlyTable`. Owns the page/pageSize
 * state and returns a `makePaginationConfig(totalItems)` builder — the total
 * usually comes from a query driven by `page`/`pageSize`, so it's supplied after
 * this hook runs rather than passed in. Navigation is clamped to the valid range.
 */
export function useOffsetPagination(initialPageSize = 10) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const makePaginationConfig = (totalItems: number): PaginationConfig => {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const clampPage = (p: number) => Math.min(totalPages, Math.max(1, p));
    return {
      enabled: true,
      pagination: {
        currentPage,
        pageSize,
        totalItems,
        startItem: totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1,
        endItem: Math.min(currentPage * pageSize, totalItems),
        hasNextPage: currentPage < totalPages,
        hasPreviousPage: currentPage > 1,
      },
      onPageSizeChange: (size) => {
        setPageSize(size);
        setCurrentPage(1);
      },
      onPreviousPage: () => setCurrentPage(clampPage(currentPage - 1)),
      onNextPage: () => setCurrentPage(clampPage(currentPage + 1)),
      onFirstPage: () => setCurrentPage(1),
      onLastPage: () => setCurrentPage(totalPages),
      onGoToPage: (p) => setCurrentPage(clampPage(p)),
      showRowsPerPage: true,
      showPageInfo: true,
      showPageNumbers: true,
    };
  };

  /**
   * Call during render once the query has succeeded: a refetch that shrinks
   * the total can leave the current page past the end.
   */
  const keepPageInRange = (totalItems: number) => {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    if (currentPage > totalPages) setCurrentPage(totalPages);
  };

  return {
    page: currentPage,
    pageSize,
    makePaginationConfig,
    keepPageInRange,
    /** Back to page one, for when the filters behind `totalItems` change. */
    resetPage: () => setCurrentPage(1),
  };
}
